import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  doc,
  getDoc,
  runTransaction,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../rbac/AuthContext";

const ROOM_TYPES = [
  "1RK",
  "1BHK",
  "2BHK",
  "2.5BHK",
  "3BHK",
  "3.5BHK",
  "4BHK",
  "4.5BHK",
  "5BHK",
  "Penthouse",
  "Shop",
  "Office",
  "Other",
];

const DEFAULT_UNIT = {
  roomType: "2BHK",
  squareFeet: "",
  calculationType: "perSqft",
  ratePerSqft: "",
  fixedMaintenanceAmount: "",
  isActive: true,
};

const DEFAULT_SERIES_FORM = {
  series: "",
  roomType: "2BHK",
  squareFeet: "",
  calculationType: "perSqft",
  ratePerSqft: "",
  fixedMaintenanceAmount: "",
  isActive: true,
};

const DEFAULT_BULK_FORM = {
  updateMode: "series",
  seriesNumber: "",
  floorNumber: "",
  currentSquareFeet: "",

  updateRoomType: false,
  roomType: "2BHK",

  updateSquareFeet: false,
  squareFeet: "",

  updateCalculationType: false,
  calculationType: "perSqft",

  updatePrice: false,
  ratePerSqft: "",
  fixedMaintenanceAmount: "",

  updateStatus: false,
  isActive: true,
};

const toSafeNumber = (value) => {
  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue)) {
    return 0;
  }

  return parsedValue;
};

const calculateMaintenance = (unit) => {
  if (unit.calculationType === "fixed") {
    return toSafeNumber(unit.fixedMaintenanceAmount);
  }

  return toSafeNumber(unit.squareFeet) * toSafeNumber(unit.ratePerSqft);
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(toSafeNumber(value));
};

const createUnitDocumentId = (flatNumber) => {
  return String(flatNumber || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
};

// const buildFlatDetail = (
//   formValue,
//   currentUserId,
//   existingFlatDetail
// ) => {
//   const squareFeet = toSafeNumber(
//     formValue.squareFeet
//   );

//   const ratePerSqft = toSafeNumber(
//     formValue.ratePerSqft
//   );

//   const fixedMaintenanceAmount =
//     toSafeNumber(
//       formValue.fixedMaintenanceAmount
//     );

//   const calculationType =
//     formValue.calculationType === "fixed"
//       ? "fixed"
//       : "perSqft";

//   const maintenanceAmount =
//     calculationType === "fixed"
//       ? fixedMaintenanceAmount
//       : squareFeet * ratePerSqft;

//   const now = Timestamp.now();

//   return {
//     roomType:
//       formValue.roomType || "2BHK",

//     squareFeet,

//     calculationType,

//     ratePerSqft:
//       calculationType === "perSqft"
//         ? ratePerSqft
//         : 0,

//     fixedMaintenanceAmount:
//       calculationType === "fixed"
//         ? fixedMaintenanceAmount
//         : 0,

//     maintenanceAmount,

//     isActive:
//       formValue.isActive !== false,

//     createdAt:
//       existingFlatDetail?.createdAt ||
//       now,

//     createdBy:
//       existingFlatDetail?.createdBy ||
//       currentUserId ||
//       null,

//     updatedAt: now,

//     updatedBy:
//       currentUserId || null,
//   };
// };


const buildFlatDetail = (
  formValue,
  currentUserId,
  existingFlatDetail
) => {
  const safeFormValue = {
    ...DEFAULT_UNIT,
    ...(formValue || {}),
  };

  const squareFeet = toSafeNumber(
    safeFormValue.squareFeet
  );

  const ratePerSqft = toSafeNumber(
    safeFormValue.ratePerSqft
  );

  const fixedMaintenanceAmount =
    toSafeNumber(
      safeFormValue.fixedMaintenanceAmount
    );

  const calculationType =
    safeFormValue.calculationType === "fixed"
      ? "fixed"
      : "perSqft";

  const maintenanceAmount =
    calculationType === "fixed"
      ? fixedMaintenanceAmount
      : squareFeet * ratePerSqft;

  const now = Timestamp.now();

  return {
    roomType:
      safeFormValue.roomType || "2BHK",

    bhkType:
      safeFormValue.roomType || "2BHK",

    squareFeet,

    calculationType,

    ratePerSqft:
      calculationType === "perSqft"
        ? ratePerSqft
        : 0,

    fixedMaintenanceAmount:
      calculationType === "fixed"
        ? fixedMaintenanceAmount
        : 0,

    fixedAmount:
      calculationType === "fixed"
        ? fixedMaintenanceAmount
        : 0,

    maintenanceAmount,

    monthlyMaintenance:
      maintenanceAmount,

    isActive:
      safeFormValue.isActive !== false,

    createdAt:
      existingFlatDetail?.createdAt ||
      now,

    createdBy:
      existingFlatDetail?.createdBy ||
      currentUserId ||
      null,

    updatedAt: now,

    updatedBy:
      currentUserId || null,
  };
};
const extractSeriesNumber = (flatNumber) => {
  const flatText = String(flatNumber || "").trim();

  const numericMatches = flatText.match(/\d+/g);

  if (!numericMatches || numericMatches.length === 0) {
    return "";
  }

  const numericPart = numericMatches[numericMatches.length - 1];

  const numericValue = Number(numericPart);

  if (!Number.isFinite(numericValue)) {
    return "";
  }

  const seriesNumber = numericValue % 100;

  return String(seriesNumber).padStart(2, "0");
};

const extractFloorNumber = (flatNumber) => {
  const flatText = String(flatNumber || "").trim();

  const numericMatches = flatText.match(/\d+/g);

  if (!numericMatches || numericMatches.length === 0) {
    return "";
  }

  const numericPart = numericMatches[numericMatches.length - 1];

  if (numericPart.length < 3) {
    return "";
  }

  const floorPart = numericPart.slice(0, -2);

  const floorNumber = Number(floorPart);

  if (!Number.isFinite(floorNumber) || floorNumber <= 0) {
    return "";
  }

  return String(floorNumber);
};

const sortSeriesNumbers = (firstSeries, secondSeries) => {
  return Number(firstSeries) - Number(secondSeries);
};

export default function CommunityMaintenanceEditor({
  show,
  onHide,
  communityId,
  block,
}) {
  const { currentUser, isAdmin, isManager } = useAuth();

  const canManage = Boolean(isAdmin || isManager);

  const [unitForms, setUnitForms] = useState({});
//   const [configuredFlats, setExistingUnitIds] = useState(new Set());
const [configuredFlats, setConfiguredFlats] =
  useState(new Set());

  const [seriesForm, setSeriesForm] = useState(DEFAULT_SERIES_FORM);

  const [selectedSeriesFilter, setSelectedSeriesFilter] = useState("");

  const [searchText, setSearchText] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [bulkForm, setBulkForm] = useState(DEFAULT_BULK_FORM);

  const [selectedFlats, setSelectedFlats] = useState(new Set());

  const blockFlats = useMemo(() => {
    if (!Array.isArray(block?.flats)) {
      return [];
    }

    return block.flats;
  }, [block]);

  const availableSeries = useMemo(() => {
    const seriesSet = new Set();

    blockFlats.forEach((flatNumber) => {
      const seriesNumber = extractSeriesNumber(flatNumber);

      if (seriesNumber) {
        seriesSet.add(seriesNumber);
      }
    });

    return Array.from(seriesSet).sort(sortSeriesNumbers);
  }, [blockFlats]);

  const availableFloors = useMemo(() => {
    const floorSet = new Set();

    blockFlats.forEach((flatNumber) => {
      const floorNumber = extractFloorNumber(flatNumber);

      if (floorNumber) {
        floorSet.add(floorNumber);
      }
    });

    return Array.from(floorSet).sort(
      (firstFloor, secondFloor) => Number(firstFloor) - Number(secondFloor),
    );
  }, [blockFlats]);

  const seriesSummary = useMemo(() => {
    const summary = {};

    availableSeries.forEach((seriesNumber) => {
      const seriesFlats = blockFlats.filter(
        (flatNumber) => extractSeriesNumber(flatNumber) === seriesNumber,
      );

     const configuredCount = seriesFlats.filter(
  (flatNumber) =>
    configuredFlats.has(flatNumber)
).length;

      summary[seriesNumber] = {
        total: seriesFlats.length,
        configured: configuredCount,
      };
    });

    return summary;
  }, [availableSeries, blockFlats, configuredFlats]);

  const visibleFlats = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    return blockFlats.filter((flatNumber) => {
      const seriesNumber = extractSeriesNumber(flatNumber);

      const matchesSeries =
        !selectedSeriesFilter || seriesNumber === selectedSeriesFilter;

      const matchesSearch =
        !normalizedSearch ||
        String(flatNumber).toLowerCase().includes(normalizedSearch);

      return matchesSeries && matchesSearch;
    });
  }, [blockFlats, selectedSeriesFilter, searchText]);

  const visibleMaintenanceTotal = useMemo(() => {
    return visibleFlats.reduce((totalAmount, flatNumber) => {
      const unit = unitForms[flatNumber];

      if (!unit || unit.isActive === false) {
        return totalAmount;
      }

      return totalAmount + calculateMaintenance(unit);
    }, 0);
  }, [visibleFlats, unitForms]);

//   useEffect(() => {
//     if (!show || !communityId || !block) {
//       return;
//     }

//     const loadUnitDetails = async () => {
//       try {
//         setLoading(true);
//         setError("");
//         setSuccess("");
//         setSearchText("");
//         setSelectedSeriesFilter("");

//         const unitsRef = collection(db, "communities", communityId, "units");

//         const snapshot = await getDocs(unitsRef);

//         const savedUnitsByFlat = {};
//         const savedIds = new Set();

//         snapshot.forEach((documentSnapshot) => {
//           const unitData = documentSnapshot.data() || {};

//           const belongsToCurrentBlock =
//             unitData.blockId === block.id || unitData.blockName === block.name;

//           if (belongsToCurrentBlock && unitData.flatNumber) {
//             savedUnitsByFlat[unitData.flatNumber] = {
//               roomType: unitData.roomType || "2BHK",

//               squareFeet: unitData.squareFeet ?? "",

//               calculationType: unitData.calculationType || "perSqft",

//               ratePerSqft: unitData.ratePerSqft ?? "",

//               fixedMaintenanceAmount: unitData.fixedMaintenanceAmount ?? "",

//               isActive: unitData.isActive !== false,
//             };

//             savedIds.add(documentSnapshot.id);
//           }
//         });

//         const initialForms = {};

//         blockFlats.forEach((flatNumber) => {
//           initialForms[flatNumber] = {
//             ...DEFAULT_UNIT,
//             ...(savedUnitsByFlat[flatNumber] || {}),
//           };
//         });

//         setUnitForms(initialForms);
//         setExistingUnitIds(savedIds);

//         const firstSeries =
//           availableSeries.length > 0 ? availableSeries[0] : "";

//         setSeriesForm({
//           ...DEFAULT_SERIES_FORM,
//           series: firstSeries,
//         });

//         const firstFloor = availableFloors.length > 0 ? availableFloors[0] : "";

//         setBulkForm({
//           ...DEFAULT_BULK_FORM,
//           seriesNumber: firstSeries,
//           floorNumber: firstFloor,
//         });

//         setSelectedFlats(new Set());
//       } catch (loadError) {
//         console.error("Failed to load apartment details:", loadError);

//         setError(loadError?.message || "Failed to load apartment details.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     loadUnitDetails();
//   }, [show, communityId, block, blockFlats, availableSeries, availableFloors]);



  useEffect(() => {
  if (!show || !communityId || !block) {
    return;
  }

  const loadFlatDetails = async () => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");
      setSearchText("");
      setSelectedSeriesFilter("");
      setSelectedFlats(new Set());

      const communityRef = doc(
        db,
        "communities",
        communityId
      );

      const communitySnapshot =
        await getDoc(communityRef);

      if (!communitySnapshot.exists()) {
        throw new Error(
          "Community not found."
        );
      }

      const communityData =
        communitySnapshot.data() || {};

      const communityBlocks =
        Array.isArray(communityData.blocks)
          ? communityData.blocks
          : [];

      const currentBlock =
        communityBlocks.find(
          (savedBlock) =>
            savedBlock.id === block.id
        ) ||
        communityBlocks.find(
          (savedBlock) =>
            String(savedBlock.name || "")
              .trim()
              .toLowerCase() ===
            String(block.name || "")
              .trim()
              .toLowerCase()
        );

      if (!currentBlock) {
        throw new Error(
          "Selected block was not found."
        );
      }

      const currentFlats =
        Array.isArray(currentBlock.flats)
          ? currentBlock.flats
          : [];

      const savedFlatDetails =
        currentBlock.flatDetails &&
        typeof currentBlock.flatDetails ===
          "object"
          ? currentBlock.flatDetails
          : {};

      const initialForms = {};
      const configured = new Set();

      currentFlats.forEach((flatNumber) => {
        const savedDetail =
          savedFlatDetails[flatNumber];

       initialForms[flatNumber] = {
  ...DEFAULT_UNIT,
  ...(savedDetail || {}),

  roomType:
    savedDetail?.roomType ||
    savedDetail?.bhkType ||
    "2BHK",

  squareFeet:
    savedDetail?.squareFeet ?? "",

  calculationType:
    savedDetail?.calculationType ||
    "perSqft",

  ratePerSqft:
    savedDetail?.ratePerSqft ?? "",

  fixedMaintenanceAmount:
    savedDetail
      ?.fixedMaintenanceAmount ??
    savedDetail?.fixedAmount ??
    "",

  isActive:
    savedDetail?.isActive !== false,
};
        if (savedDetail) {
          configured.add(flatNumber);
        }
      });

      setUnitForms(initialForms);
      setConfiguredFlats(configured);

      const firstSeries =
        availableSeries.length > 0
          ? availableSeries[0]
          : "";

      const firstFloor =
        availableFloors.length > 0
          ? availableFloors[0]
          : "";

      setSeriesForm({
        ...DEFAULT_SERIES_FORM,
        series: firstSeries,
      });

      setBulkForm({
        ...DEFAULT_BULK_FORM,
        seriesNumber: firstSeries,
        floorNumber: firstFloor,
      });
    } catch (loadError) {
      console.error(
        "Failed to load flat details:",
        loadError
      );

      setError(
        loadError?.message ||
          "Failed to load flat details."
      );
    } finally {
      setLoading(false);
    }
  };

  loadFlatDetails();
}, [
  show,
  communityId,
  block,
  availableSeries,
  availableFloors,
]);

const updateUnitField = (
  flatNumber,
  field,
  value
) => {
  setUnitForms((previousForms) => ({
    ...previousForms,

   [flatNumber] : {
      ...DEFAULT_UNIT,
      ...(previousForms[flatNumber] || {}),
      value,
    },
  }));

  setError("");
  setSuccess("");
};

  const updateSeriesFormField = (field, value) => {
    setSeriesForm((previousForm) => ({
      ...previousForm,
      [field]: value,
    }));

    setError("");
    setSuccess("");
  };

  const updateBulkFormField = (field, value) => {
    setBulkForm((previousForm) => ({
      ...previousForm,
      [field]: value,
    }));

    setError("");
    setSuccess("");
  };

  const toggleFlatSelection = (flatNumber) => {
    setSelectedFlats((previousSelection) => {
      const nextSelection = new Set(previousSelection);

      if (nextSelection.has(flatNumber)) {
        nextSelection.delete(flatNumber);
      } else {
        nextSelection.add(flatNumber);
      }

      return nextSelection;
    });
  };

  const areAllVisibleFlatsSelected =
    visibleFlats.length > 0 &&
    visibleFlats.every((flatNumber) => selectedFlats.has(flatNumber));

  const toggleAllVisibleFlats = () => {
    setSelectedFlats((previousSelection) => {
      const nextSelection = new Set(previousSelection);

      if (areAllVisibleFlatsSelected) {
        visibleFlats.forEach((flatNumber) => {
          nextSelection.delete(flatNumber);
        });
      } else {
        visibleFlats.forEach((flatNumber) => {
          nextSelection.add(flatNumber);
        });
      }

      return nextSelection;
    });
  };

  const clearSelectedFlats = () => {
    setSelectedFlats(new Set());
    setError("");
    setSuccess("");
  };

  const bulkTargetFlats = useMemo(() => {
    if (bulkForm.updateMode === "all") {
      return blockFlats;
    }

    if (bulkForm.updateMode === "selected") {
      return blockFlats.filter((flatNumber) => selectedFlats.has(flatNumber));
    }

    if (bulkForm.updateMode === "series") {
      if (!bulkForm.seriesNumber) {
        return [];
      }

      return blockFlats.filter(
        (flatNumber) =>
          extractSeriesNumber(flatNumber) === bulkForm.seriesNumber,
      );
    }

    if (bulkForm.updateMode === "floor") {
      if (!bulkForm.floorNumber) {
        return [];
      }

      return blockFlats.filter(
        (flatNumber) => extractFloorNumber(flatNumber) === bulkForm.floorNumber,
      );
    }

    if (bulkForm.updateMode === "squareFeet") {
      const targetSquareFeet = toSafeNumber(bulkForm.currentSquareFeet);

      if (targetSquareFeet <= 0) {
        return [];
      }

      return blockFlats.filter((flatNumber) => {
        const unit = unitForms[flatNumber];

        return unit && toSafeNumber(unit.squareFeet) === targetSquareFeet;
      });
    }

    return [];
  }, [
    blockFlats,
    bulkForm.updateMode,
    bulkForm.seriesNumber,
    bulkForm.floorNumber,
    bulkForm.currentSquareFeet,
    selectedFlats,
    unitForms,
  ]);

  const validateBulkForm = () => {
    if (bulkForm.updateMode === "series" && !bulkForm.seriesNumber) {
      return "Select a series.";
    }

    if (bulkForm.updateMode === "floor" && !bulkForm.floorNumber) {
      return "Select a floor.";
    }

    if (
      bulkForm.updateMode === "squareFeet" &&
      toSafeNumber(bulkForm.currentSquareFeet) <= 0
    ) {
      return "Enter the current square feet to match.";
    }

    if (bulkForm.updateMode === "selected" && selectedFlats.size === 0) {
      return "Select at least one flat using the checkboxes.";
    }

    if (bulkTargetFlats.length === 0) {
      return "No flats matched the selected option.";
    }

    const hasFieldToUpdate =
      bulkForm.updateRoomType ||
      bulkForm.updateSquareFeet ||
      bulkForm.updateCalculationType ||
      bulkForm.updatePrice ||
      bulkForm.updateStatus;

    if (!hasFieldToUpdate) {
      return "Select at least one field to update.";
    }

    if (bulkForm.updateSquareFeet && toSafeNumber(bulkForm.squareFeet) <= 0) {
      return "New square feet must be greater than zero.";
    }

    if (bulkForm.updatePrice) {
      if (
        bulkForm.calculationType === "perSqft" &&
        toSafeNumber(bulkForm.ratePerSqft) <= 0
      ) {
        return "Rate per square foot must be greater than zero.";
      }

      if (
        bulkForm.calculationType === "fixed" &&
        toSafeNumber(bulkForm.fixedMaintenanceAmount) <= 0
      ) {
        return "Fixed maintenance amount must be greater than zero.";
      }
    }

    return "";
  };

  const applyBulkConfiguration = () => {
    if (!canManage) {
      setError("Only an admin or manager can update apartment details.");
      setSuccess("");
      return;
    }

    const validationError = validateBulkForm();

    if (validationError) {
      setError(validationError);
      setSuccess("");
      return;
    }

    setUnitForms((previousForms) => {
      const nextForms = {
        ...previousForms,
      };

      bulkTargetFlats.forEach((flatNumber) => {
        const currentUnit = {
          ...DEFAULT_UNIT,
          ...(previousForms[flatNumber] || {}),
        };

        const updatedUnit = {
          ...currentUnit,
        };

        if (bulkForm.updateRoomType) {
          updatedUnit.roomType = bulkForm.roomType;
        }

        if (bulkForm.updateSquareFeet) {
          updatedUnit.squareFeet = bulkForm.squareFeet;
        }

      if (
  bulkForm.updateCalculationType ||
  bulkForm.updatePrice
) {
  updatedUnit.calculationType =
    bulkForm.calculationType;
}

if (bulkForm.updatePrice) {
  if (
    bulkForm.calculationType === "fixed"
  ) {
    updatedUnit.fixedMaintenanceAmount =
      bulkForm.fixedMaintenanceAmount;

    updatedUnit.ratePerSqft = "";
  } else {
    updatedUnit.ratePerSqft =
      bulkForm.ratePerSqft;

    updatedUnit.fixedMaintenanceAmount = "";
  }
}

        const finalCalculationType = bulkForm.updateCalculationType
          ? bulkForm.calculationType
          : updatedUnit.calculationType;

        if (bulkForm.updatePrice) {
          if (finalCalculationType === "fixed") {
            updatedUnit.fixedMaintenanceAmount =
              bulkForm.fixedMaintenanceAmount;

            updatedUnit.ratePerSqft = "";
          } else {
            updatedUnit.ratePerSqft = bulkForm.ratePerSqft;

            updatedUnit.fixedMaintenanceAmount = "";
          }
        }

        if (bulkForm.updateStatus) {
          updatedUnit.isActive = bulkForm.isActive;
        }

        nextForms[flatNumber] = updatedUnit;
      });

      return nextForms;
    });

    setError("");

    setSuccess(
      `${bulkTargetFlats.length} flats updated in the form. Review the values and click Save All Changes.`,
    );
  };

  const getSeriesFlatNumbers = (seriesNumber) => {
    return blockFlats.filter(
      (flatNumber) => extractSeriesNumber(flatNumber) === seriesNumber,
    );
  };

  const validateSeriesForm = () => {
    if (!seriesForm.series) {
      return "Select a flat series.";
    }

    if (!seriesForm.roomType) {
      return "Select a room type.";
    }

    if (toSafeNumber(seriesForm.squareFeet) <= 0) {
      return "Square feet must be greater than zero.";
    }

    if (
      seriesForm.calculationType === "perSqft" &&
      toSafeNumber(seriesForm.ratePerSqft) <= 0
    ) {
      return "Rate per square foot must be greater than zero.";
    }

    if (
      seriesForm.calculationType === "fixed" &&
      toSafeNumber(seriesForm.fixedMaintenanceAmount) <= 0
    ) {
      return "Fixed maintenance amount must be greater than zero.";
    }

    return "";
  };

  const applySeriesConfiguration = () => {
    const validationError = validateSeriesForm();

    if (validationError) {
      setError(validationError);
      setSuccess("");
      return;
    }

    const matchingFlats = getSeriesFlatNumbers(seriesForm.series);

    if (matchingFlats.length === 0) {
      setError(`No flats found for series ${seriesForm.series}.`);
      setSuccess("");
      return;
    }

    setUnitForms((previousForms) => {
      const nextForms = {
        ...previousForms,
      };

      matchingFlats.forEach((flatNumber) => {
        nextForms[flatNumber] = {
          ...nextForms[flatNumber],

          roomType: seriesForm.roomType,

          squareFeet: seriesForm.squareFeet,

          calculationType: seriesForm.calculationType,

          ratePerSqft: seriesForm.ratePerSqft,

          fixedMaintenanceAmount: seriesForm.fixedMaintenanceAmount,

          isActive: seriesForm.isActive,
        };
      });

      return nextForms;
    });

    setSelectedSeriesFilter(seriesForm.series);

    setError("");

    setSuccess(
      `Series ${seriesForm.series} values applied to ${matchingFlats.length} flats. Review the flats and click Save All Changes.`,
    );
  };

  const loadFirstFlatIntoSeriesForm = () => {
    if (!seriesForm.series) {
      setError("Select a series first.");
      return;
    }

    const matchingFlats = getSeriesFlatNumbers(seriesForm.series);

    if (matchingFlats.length === 0) {
      setError(`No flats found for series ${seriesForm.series}.`);
      return;
    }

    const firstFlatNumber = matchingFlats[0];

    const firstFlatValue = unitForms[firstFlatNumber];

    if (!firstFlatValue) {
      setError("Apartment details are not available.");
      return;
    }

    setSeriesForm({
      series: seriesForm.series,
      roomType: firstFlatValue.roomType || "2BHK",
      squareFeet: firstFlatValue.squareFeet ?? "",
      calculationType: firstFlatValue.calculationType || "perSqft",
      ratePerSqft: firstFlatValue.ratePerSqft ?? "",
      fixedMaintenanceAmount: firstFlatValue.fixedMaintenanceAmount ?? "",
      isActive: firstFlatValue.isActive !== false,
    });

    setError("");

    setSuccess(`Loaded values from ${firstFlatNumber}.`);
  };

  const applyOneFlatToSeries = (sourceFlatNumber) => {
    const sourceUnit = unitForms[sourceFlatNumber];

    if (!sourceUnit) {
      setError(`Details not found for ${sourceFlatNumber}.`);
      return;
    }

    const seriesNumber = extractSeriesNumber(sourceFlatNumber);

    const matchingFlats = getSeriesFlatNumbers(seriesNumber);

    setUnitForms((previousForms) => {
      const nextForms = {
        ...previousForms,
      };

      matchingFlats.forEach((flatNumber) => {
        nextForms[flatNumber] = {
          ...nextForms[flatNumber],
          roomType: sourceUnit.roomType,
          squareFeet: sourceUnit.squareFeet,
          calculationType: sourceUnit.calculationType,
          ratePerSqft: sourceUnit.ratePerSqft,
          fixedMaintenanceAmount: sourceUnit.fixedMaintenanceAmount,
          isActive: sourceUnit.isActive,
        };
      });

      return nextForms;
    });

    setSelectedSeriesFilter(seriesNumber);
    setError("");

    setSuccess(
      `${sourceFlatNumber} values applied to all ${seriesNumber} series flats.`,
    );
  };

//   const validateAllUnits = () => {
//     if (!communityId) {
//       return "Missing community ID.";
//     }

//     if (!block?.name) {
//       return "Missing block information.";
//     }

//     if (blockFlats.length === 0) {
//       return "No flats are available in this block.";
//     }

//     for (const flatNumber of blockFlats) {
//       const unit = unitForms[flatNumber];

//       if (!unit) {
//         return `Missing details for ${flatNumber}.`;
//       }

//       if (!unit.roomType) {
//         return `Select room type for ${flatNumber}.`;
//       }

//       if (toSafeNumber(unit.squareFeet) <= 0) {
//         return `Square feet must be greater than zero for ${flatNumber}.`;
//       }

//       if (
//         unit.calculationType === "perSqft" &&
//         toSafeNumber(unit.ratePerSqft) <= 0
//       ) {
//         return `Rate per square foot must be greater than zero for ${flatNumber}.`;
//       }

//       if (
//         unit.calculationType === "fixed" &&
//         toSafeNumber(unit.fixedMaintenanceAmount) <= 0
//       ) {
//         return `Fixed maintenance must be greater than zero for ${flatNumber}.`;
//       }
//     }

//     return "";
//   };

//   const saveAllUnits = async () => {
//     if (!canManage) {
//       setError("Only an admin or manager can update apartment details.");
//       return;
//     }

//     const validationError = validateAllUnits();

//     if (validationError) {
//       setError(validationError);
//       setSuccess("");
//       return;
//     }

//     if (blockFlats.length > 450) {
//       setError(
//         "This block contains too many flats for one save operation. Save the data in smaller groups.",
//       );
//       return;
//     }

//     try {
//       setSaving(true);
//       setError("");
//       setSuccess("");

//       const batch = writeBatch(db);

//       blockFlats.forEach((flatNumber) => {
//         const formValue = unitForms[flatNumber];

//         const documentId = createUnitDocumentId(flatNumber);

//         const unitRef = doc(
//           db,
//           "communities",
//           communityId,
//           "units",
//           documentId,
//         );

//         const squareFeet = toSafeNumber(formValue.squareFeet);

//         const ratePerSqft = toSafeNumber(formValue.ratePerSqft);

//         const fixedMaintenanceAmount = toSafeNumber(
//           formValue.fixedMaintenanceAmount,
//         );

//         const maintenanceAmount = calculateMaintenance({
//           calculationType: formValue.calculationType,
//           squareFeet,
//           ratePerSqft,
//           fixedMaintenanceAmount,
//         });

//         //         const unitData = {
//         //           communityId,

//         //           blockId: block.id || "",

//         //           blockName: block.name,

//         //           flatNumber,

//         //           floorNumber:
//         //   toSafeNumber(
//         //     extractFloorNumber(flatNumber)
//         //   ),

//         // seriesNumber:
//         //   toSafeNumber(
//         //     extractSeriesNumber(flatNumber)
//         //   ),

//         // seriesLabel:
//         //   extractSeriesNumber,

//         //           roomType: formValue.roomType,

//         //           squareFeet,

//         //           calculationType: formValue.calculationType,

//         //           ratePerSqft:
//         //             formValue.calculationType === "perSqft" ? ratePerSqft : 0,

//         //           fixedMaintenanceAmount:
//         //             formValue.calculationType === "fixed" ? fixedMaintenanceAmount : 0,

//         //           maintenanceAmount,

//         //           isActive: Boolean(formValue.isActive),

//         //           updatedAt: serverTimestamp(),

//         //           updatedBy: currentUser?.uid || null,
//         //         };

//         const floorLabel = extractFloorNumber(flatNumber);
//         const seriesLabel = extractSeriesNumber(flatNumber);

//         const unitData = {
//           communityId,

//           blockId: block.id || "",

//           blockName: block.name,

//           flatNumber,

//           floorNumber: floorLabel ? toSafeNumber(floorLabel) : 0,

//           floorLabel,

//           seriesNumber: seriesLabel ? toSafeNumber(seriesLabel) : 0,

//           seriesLabel,

//           roomType: formValue.roomType,

//           squareFeet,

//           calculationType: formValue.calculationType,

//           ratePerSqft:
//             formValue.calculationType === "perSqft" ? ratePerSqft : 0,

//           fixedMaintenanceAmount:
//             formValue.calculationType === "fixed" ? fixedMaintenanceAmount : 0,

//           maintenanceAmount,

//           isActive: Boolean(formValue.isActive),

//           updatedAt: serverTimestamp(),

//           updatedBy: currentUser?.uid || null,
//         };

//         if (!configuredFlats.has(documentId)) {
//           unitData.createdAt = serverTimestamp();

//           unitData.createdBy = currentUser?.uid || null;
//         }

//         batch.set(unitRef, unitData, {
//           merge: true,
//         });
//       });
//       console.log(
//   "Current Role",
//   currentUser?.uid
// );

//       await batch.commit();

//       const savedDocumentIds = new Set(
//         blockFlats.map((flatNumber) => createUnitDocumentId(flatNumber)),
//       );

//       setExistingUnitIds(savedDocumentIds);

//       setSuccess(
//         `${blockFlats.length} apartment maintenance records saved successfully.`,
//       );
//     } catch (saveError) {
//       console.error("Failed to save apartment details:", saveError);

//       setError(saveError?.message || "Failed to save apartment details.");
//     } finally {
//       setSaving(false);
//     }
//   };

const saveAllUnits = async () => {
  if (!canManage) {
    setError(
      "Only an admin or manager can update apartment details."
    );
    setSuccess("");
    return;
  }

  try {
    setSaving(true);
    setError("");
    setSuccess("");

    const communityRef = doc(
      db,
      "communities",
      communityId
    );

    await runTransaction(
      db,
      async (transaction) => {
        const communitySnapshot =
          await transaction.get(
            communityRef
          );

        if (
          !communitySnapshot.exists()
        ) {
          throw new Error(
            "Community not found."
          );
        }

        const communityData =
          communitySnapshot.data() || {};

        const existingBlocks =
          Array.isArray(
            communityData.blocks
          )
            ? communityData.blocks
            : [];

        const targetBlockIndex =
          existingBlocks.findIndex(
            (savedBlock) =>
              savedBlock.id === block.id
          );

        const fallbackBlockIndex =
          existingBlocks.findIndex(
            (savedBlock) =>
              String(
                savedBlock.name || ""
              )
                .trim()
                .toLowerCase() ===
              String(block.name || "")
                .trim()
                .toLowerCase()
          );

        const blockIndex =
          targetBlockIndex >= 0
            ? targetBlockIndex
            : fallbackBlockIndex;

        if (blockIndex < 0) {
          throw new Error(
            "Selected block was not found."
          );
        }

        const currentBlock =
          existingBlocks[blockIndex];

        const currentFlatDetails =
          currentBlock.flatDetails &&
          typeof currentBlock
            .flatDetails === "object"
            ? currentBlock.flatDetails
            : {};

        const nextFlatDetails = {
          ...currentFlatDetails,
        };

        blockFlats.forEach(
          (flatNumber) => {
            const formValue =
              unitForms[flatNumber];

            nextFlatDetails[
              flatNumber
            ] = buildFlatDetail(
              formValue,
              currentUser?.uid,
              currentFlatDetails[
                flatNumber
              ]
            );
          }
        );

        const nextBlocks =
          existingBlocks.map(
            (savedBlock, index) => {
              if (index !== blockIndex) {
                return savedBlock;
              }

              return {
                ...savedBlock,

                flatDetails:
                  nextFlatDetails,

                maintenanceConfigured:
                  true,

                maintenanceUpdatedAt:
                  Timestamp.now(),

                maintenanceUpdatedBy:
                  currentUser?.uid ||
                  null,
              };
            }
          );

        transaction.update(
          communityRef,
          {
            blocks: nextBlocks,
          }
        );
      }
    );

    setConfiguredFlats(
      new Set(blockFlats)
    );

    setSuccess(
      `${blockFlats.length} apartment details saved inside Block ${block.name}.`
    );
  } catch (saveError) {
    console.error(
      "Failed to save apartment details:",
      saveError
    );

    setError(
      saveError?.message ||
        "Failed to save apartment details."
    );
  } finally {
    setSaving(false);
  }
};

  const handleClose = () => {
    if (saving) {
      return;
    }

    setError("");
    setSuccess("");
    setSearchText("");
    setSelectedSeriesFilter("");
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      scrollable
      fullscreen="md-down"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          Apartment Details
          {block?.name ? ` - Block ${block.name}` : ""}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {success && <Alert variant="success">{success}</Alert>}

       <Alert variant="info">
  BHK type, square feet and maintenance details will be
  saved inside the selected block under flatDetails.
  Existing flat numbers will remain unchanged.
</Alert>

        {loading ? (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />

            <span>Loading apartment details...</span>
          </div>
        ) : blockFlats.length === 0 ? (
          <Alert variant="warning">
            This block does not contain generated flat numbers. Edit and save
            the block once to generate the flats array.
          </Alert>
        ) : (
          <>
            <Card className="mb-3 border-primary">
              <Card.Header>
                <strong>Advanced Bulk Update</strong>
              </Card.Header>

              <Card.Body>
                <Row className="g-3">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Select Flats By</Form.Label>

                      <Form.Select
                        value={bulkForm.updateMode}
                        onChange={(event) =>
                          updateBulkFormField("updateMode", event.target.value)
                        }
                        disabled={!canManage}
                      >
                        <option value="series">Series Wise</option>

                        <option value="floor">Floor Wise</option>

                        <option value="squareFeet">
                          Current Square Feet Wise
                        </option>

                        <option value="selected">Selected Checkboxes</option>

                        <option value="all">All Flats</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    {bulkForm.updateMode === "series" && (
                      <Form.Group>
                        <Form.Label>Series</Form.Label>

                        <Form.Select
                          value={bulkForm.seriesNumber}
                          onChange={(event) =>
                            updateBulkFormField(
                              "seriesNumber",
                              event.target.value,
                            )
                          }
                          disabled={!canManage}
                        >
                          <option value="">Select series</option>

                          {availableSeries.map((seriesNumber) => (
                            <option key={seriesNumber} value={seriesNumber}>
                              Series {seriesNumber}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    )}

                    {bulkForm.updateMode === "floor" && (
                      <Form.Group>
                        <Form.Label>Floor</Form.Label>

                        <Form.Select
                          value={bulkForm.floorNumber}
                          onChange={(event) =>
                            updateBulkFormField(
                              "floorNumber",
                              event.target.value,
                            )
                          }
                          disabled={!canManage}
                        >
                          <option value="">Select floor</option>

                          {availableFloors.map((floorNumber) => (
                            <option key={floorNumber} value={floorNumber}>
                              Floor {floorNumber}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    )}

                    {bulkForm.updateMode === "squareFeet" && (
                      <Form.Group>
                        <Form.Label>Current Square Feet</Form.Label>

                        <Form.Control
                          type="number"
                          min="1"
                          step="1"
                          value={bulkForm.currentSquareFeet}
                          placeholder="Example: 1200"
                          onChange={(event) =>
                            updateBulkFormField(
                              "currentSquareFeet",
                              event.target.value,
                            )
                          }
                          disabled={!canManage}
                        />
                      </Form.Group>
                    )}

                    {bulkForm.updateMode === "selected" && (
                      <Alert variant="secondary" className="mb-0">
                        Selected flats: <strong>{selectedFlats.size}</strong>
                      </Alert>
                    )}

                    {bulkForm.updateMode === "all" && (
                      <Alert variant="warning" className="mb-0">
                        All {blockFlats.length} flats will be updated.
                      </Alert>
                    )}
                  </Col>

                  <Col md={3}>
                    <Alert
                      variant={
                        bulkTargetFlats.length > 0 ? "success" : "secondary"
                      }
                      className="mb-0"
                    >
                      Matched flats: <strong>{bulkTargetFlats.length}</strong>
                    </Alert>
                  </Col>

                  <Col md={3}>
                    <Form.Label>Maintenance Preview</Form.Label>

                    <div className="form-control bg-light fw-bold">
                      {formatCurrency(calculateMaintenance(bulkForm))}
                    </div>
                  </Col>
                </Row>

                <hr />

                <Row className="g-3">
                  <Col md={6} lg={4}>
                    <Card className="h-100">
                      <Card.Body>
                        <Form.Check
                          type="checkbox"
                          id="bulk-update-room-type"
                          label="Update Room Type"
                          checked={bulkForm.updateRoomType}
                          onChange={(event) =>
                            updateBulkFormField(
                              "updateRoomType",
                              event.target.checked,
                            )
                          }
                        />

                        <Form.Select
                          className="mt-2"
                          value={bulkForm.roomType}
                          onChange={(event) =>
                            updateBulkFormField("roomType", event.target.value)
                          }
                          disabled={!canManage || !bulkForm.updateRoomType}
                        >
                          {ROOM_TYPES.map((roomType) => (
                            <option key={roomType} value={roomType}>
                              {roomType}
                            </option>
                          ))}
                        </Form.Select>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={6} lg={4}>
                    <Card className="h-100">
                      <Card.Body>
                        <Form.Check
                          type="checkbox"
                          id="bulk-update-square-feet"
                          label="Update Square Feet"
                          checked={bulkForm.updateSquareFeet}
                          onChange={(event) =>
                            updateBulkFormField(
                              "updateSquareFeet",
                              event.target.checked,
                            )
                          }
                        />

                        <Form.Control
                          className="mt-2"
                          type="number"
                          min="1"
                          step="1"
                          value={bulkForm.squareFeet}
                          placeholder="Example: 1350"
                          onChange={(event) =>
                            updateBulkFormField(
                              "squareFeet",
                              event.target.value,
                            )
                          }
                          disabled={!canManage || !bulkForm.updateSquareFeet}
                        />
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={6} lg={4}>
                    <Card className="h-100">
                      <Card.Body>
                        <Form.Check
                          type="checkbox"
                          id="bulk-update-calculation"
                          label="Update Calculation Type"
                          checked={bulkForm.updateCalculationType}
                          onChange={(event) =>
                            updateBulkFormField(
                              "updateCalculationType",
                              event.target.checked,
                            )
                          }
                        />

                        <Form.Select
                          className="mt-2"
                          value={bulkForm.calculationType}
                          onChange={(event) =>
                            updateBulkFormField(
                              "calculationType",
                              event.target.value,
                            )
                          }
                          disabled={
                            !canManage || !bulkForm.updateCalculationType
                          }
                        >
                          <option value="perSqft">Per square foot</option>

                          <option value="fixed">Fixed amount</option>
                        </Form.Select>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={6} lg={4}>
                    <Card className="h-100">
                      <Card.Body>
                        <Form.Check
                          type="checkbox"
                          id="bulk-update-price"
                          label={
                            bulkForm.calculationType === "fixed"
                              ? "Update Fixed Amount"
                              : "Update Rate Per Sq Ft"
                          }
                          checked={bulkForm.updatePrice}
                          onChange={(event) =>
                            updateBulkFormField(
                              "updatePrice",
                              event.target.checked,
                            )
                          }
                        />

                        {bulkForm.calculationType === "fixed" ? (
                          <Form.Control
                            className="mt-2"
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={bulkForm.fixedMaintenanceAmount}
                            placeholder="Example: 5000"
                            onChange={(event) =>
                              updateBulkFormField(
                                "fixedMaintenanceAmount",
                                event.target.value,
                              )
                            }
                            disabled={!canManage || !bulkForm.updatePrice}
                          />
                        ) : (
                          <Form.Control
                            className="mt-2"
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={bulkForm.ratePerSqft}
                            placeholder="Example: 4"
                            onChange={(event) =>
                              updateBulkFormField(
                                "ratePerSqft",
                                event.target.value,
                              )
                            }
                            disabled={!canManage || !bulkForm.updatePrice}
                          />
                        )}
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={6} lg={4}>
                    <Card className="h-100">
                      <Card.Body>
                        <Form.Check
                          type="checkbox"
                          id="bulk-update-status"
                          label="Update Status"
                          checked={bulkForm.updateStatus}
                          onChange={(event) =>
                            updateBulkFormField(
                              "updateStatus",
                              event.target.checked,
                            )
                          }
                        />

                        <Form.Check
                          className="mt-3"
                          type="switch"
                          id="bulk-active-status"
                          label={bulkForm.isActive ? "Active" : "Inactive"}
                          checked={bulkForm.isActive}
                          onChange={(event) =>
                            updateBulkFormField(
                              "isActive",
                              event.target.checked,
                            )
                          }
                          disabled={!canManage || !bulkForm.updateStatus}
                        />
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <div className="d-flex flex-wrap gap-2 mt-3">
                  <Button
                    variant="primary"
                    onClick={applyBulkConfiguration}
                    disabled={!canManage || bulkTargetFlats.length === 0}
                  >
                    Apply to {bulkTargetFlats.length} Flats
                  </Button>

                  {selectedFlats.size > 0 && (
                    <Button
                      variant="outline-danger"
                      onClick={clearSelectedFlats}
                    >
                      Clear Selected
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>

          

            <Row className="g-3 mb-3">
              {availableSeries.map((seriesNumber) => {
                const summary = seriesSummary[seriesNumber];

                return (
                  <Col md={4} lg={3} key={seriesNumber}>
                    <Card
                      className={
                        selectedSeriesFilter === seriesNumber
                          ? "border-primary h-100"
                          : "h-100"
                      }
                    >
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center">
                          <strong>Series {seriesNumber}</strong>

                          <Badge bg="primary">{summary?.total} flats</Badge>
                        </div>

                        <div className="text-muted mt-2">
                          Configured: {summary?.configured || 0}
                        </div>

                        <Button
                          size="sm"
                          className="mt-2"
                          variant={
                            selectedSeriesFilter === seriesNumber
                              ? "primary"
                              : "outline-primary"
                          }
                          onClick={() => setSelectedSeriesFilter(seriesNumber)}
                        >
                          View Series
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>

            <Card>
              <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <strong>Individual Apartment Overrides</strong>

                <Badge bg="dark">
                  Visible Total: {formatCurrency(visibleMaintenanceTotal)}
                </Badge>
              </Card.Header>

              <Card.Body>
                <Row className="g-2 mb-3">
                  <Col md={6}>
                    <Form.Control
                      type="search"
                      placeholder="Search flat number"
                      value={searchText}
                      onChange={(event) => setSearchText(event.target.value)}
                    />
                  </Col>

                  <Col md={4}>
                    <Form.Select
                      value={selectedSeriesFilter}
                      onChange={(event) =>
                        setSelectedSeriesFilter(event.target.value)
                      }
                    >
                      <option value="">All series</option>

                      {availableSeries.map((seriesNumber) => (
                        <option key={seriesNumber} value={seriesNumber}>
                          Series {seriesNumber}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>

                  <Col md={2}>
                    <Button
                      variant="outline-secondary"
                      className="w-100"
                      onClick={() => {
                        setSearchText("");
                        setSelectedSeriesFilter("");
                      }}
                    >
                      Clear Filter
                    </Button>
                  </Col>
                </Row>

                <Table
                  bordered
                  hover
                  responsive
                  size="sm"
                  className="align-middle"
                >
                  <thead>
                    <tr>
                      <th style={{ minWidth: 55 }}>
                        <Form.Check
                          type="checkbox"
                          id="select-all-visible-flats"
                          checked={areAllVisibleFlatsSelected}
                          onChange={toggleAllVisibleFlats}
                          aria-label="Select all visible flats"
                        />
                      </th>
                      <th
                        style={{
                          minWidth: 115,
                        }}
                      >
                        Flat
                      </th>

                      <th
                        style={{
                          minWidth: 90,
                        }}
                      >
                        Series
                      </th>

                      <th
                        style={{
                          minWidth: 140,
                        }}
                      >
                        Room Type
                      </th>

                      <th
                        style={{
                          minWidth: 130,
                        }}
                      >
                        Square Feet
                      </th>

                      <th
                        style={{
                          minWidth: 165,
                        }}
                      >
                        Calculation
                      </th>

                      <th
                        style={{
                          minWidth: 140,
                        }}
                      >
                        Rate or Amount
                      </th>

                      <th
                        style={{
                          minWidth: 140,
                        }}
                      >
                        Maintenance
                      </th>

                      <th
                        style={{
                          minWidth: 105,
                        }}
                      >
                        Status
                      </th>

                      <th
                        style={{
                          minWidth: 120,
                        }}
                      >
                        Series Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {visibleFlats.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="text-center">
                          No flats found.
                        </td>
                      </tr>
                    ) : (
                      visibleFlats.map((flatNumber) => {
                        const unit = unitForms[flatNumber] || DEFAULT_UNIT;

                        const documentId = createUnitDocumentId(flatNumber);

                        const seriesNumber = extractSeriesNumber(flatNumber);

                        const isConfigured = configuredFlats.has(flatNumber);

                        const calculatedAmount = calculateMaintenance(unit);

                        return (
                          <tr
                            key={flatNumber}
                            className={
                              selectedFlats.has(flatNumber)
                                ? "table-primary"
                                : ""
                            }
                          >
                            <td>
                              <Form.Check
                                type="checkbox"
                                id={`select-flat-${documentId}`}
                                checked={selectedFlats.has(flatNumber)}
                                onChange={() => toggleFlatSelection(flatNumber)}
                                aria-label={`Select ${flatNumber}`}
                              />
                            </td>

                            <td>
                              <div>
                                <strong>{flatNumber}</strong>
                              </div>

                              <Badge
                                bg={isConfigured ? "success" : "secondary"}
                              >
                                {isConfigured ? "Configured" : "New"}
                              </Badge>
                            </td>

                            <td>
                              <Badge bg="info">{seriesNumber}</Badge>
                            </td>

                            <td>
                              <Form.Select
                                size="sm"
                                value={unit.roomType}
                                onChange={(event) =>
                                  updateUnitField(
                                    flatNumber,
                                    "roomType",
                                    event.target.value,
                                  )
                                }
                                disabled={!canManage}
                              >
                                {ROOM_TYPES.map((roomType) => (
                                  <option key={roomType} value={roomType}>
                                    {roomType}
                                  </option>
                                ))}
                              </Form.Select>
                            </td>

                            <td>
                              <Form.Control
                                size="sm"
                                type="number"
                                min="1"
                                step="1"
                                value={unit.squareFeet}
                                placeholder="1200"
                                onChange={(event) =>
                                  updateUnitField(
                                    flatNumber,
                                    "squareFeet",
                                    event.target.value,
                                  )
                                }
                                disabled={!canManage}
                              />
                            </td>

                            <td>
                              <Form.Select
                                size="sm"
                                value={unit.calculationType}
                                onChange={(event) =>
                                  updateUnitField(
                                    flatNumber,
                                    "calculationType",
                                    event.target.value,
                                  )
                                }
                                disabled={!canManage}
                              >
                                <option value="perSqft">Per square foot</option>

                                <option value="fixed">Fixed amount</option>
                              </Form.Select>
                            </td>

                            <td>
                              {unit.calculationType === "perSqft" ? (
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  min="0.01"
                                  step="0.01"
                                  value={unit.ratePerSqft}
                                  placeholder="3"
                                  onChange={(event) =>
                                    updateUnitField(
                                      flatNumber,
                                      "ratePerSqft",
                                      event.target.value,
                                    )
                                  }
                                  disabled={!canManage}
                                />
                              ) : (
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  min="0.01"
                                  step="0.01"
                                  value={unit.fixedMaintenanceAmount}
                                  placeholder="2500"
                                  onChange={(event) =>
                                    updateUnitField(
                                      flatNumber,
                                      "fixedMaintenanceAmount",
                                      event.target.value,
                                    )
                                  }
                                  disabled={!canManage}
                                />
                              )}
                            </td>

                            <td>
                              <strong>
                                {formatCurrency(calculatedAmount)}
                              </strong>
                            </td>

                            <td>
                              <Form.Check
                                type="switch"
                                id={`unit-active-${documentId}`}
                                checked={unit.isActive}
                                label={unit.isActive ? "Active" : "Inactive"}
                                onChange={(event) =>
                                  updateUnitField(
                                    flatNumber,
                                    "isActive",
                                    event.target.checked,
                                  )
                                }
                                disabled={!canManage}
                              />
                            </td>

                            <td>
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => applyOneFlatToSeries(flatNumber)}
                                disabled={!canManage}
                              >
                                Apply to {seriesNumber}
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          disabled={saving}
        >
          Close
        </Button>

        {canManage && blockFlats.length > 0 && (
          <Button
            variant="primary"
            onClick={saveAllUnits}
            disabled={saving || loading}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              "Save All Changes"
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
